import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { fmt } from './_finHelpers';

// Rent vs. Buy Calculator — Calculator.net parity
// Layout: 2-col balanced form (Home Purchase | Home Rent + Your Information), then results + chart + table.
export default function RentVsBuyCalc() {
  // Home Purchase
  const [homePrice, setHomePrice] = useState(500000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.625);
  const [term, setTerm] = useState(30);
  const [buyClosing, setBuyClosing] = useState(2);
  const [propTax, setPropTax] = useState(1.5);
  const [propTaxInc, setPropTaxInc] = useState(3);
  const [homeIns, setHomeIns] = useState(2500);
  const [hoa, setHoa] = useState(0);
  const [maint, setMaint] = useState(1.5);
  const [appr, setAppr] = useState(3);
  const [costInc, setCostInc] = useState(3);
  const [sellClosing, setSellClosing] = useState(7);
  // Rent
  const [rent, setRent] = useState(3000);
  const [rentInc, setRentInc] = useState(3);
  const [renterIns, setRenterIns] = useState(15);
  const [deposit, setDeposit] = useState(3000);
  const [upfrontCost, setUpfrontCost] = useState(100);
  // Your info
  const [invReturn, setInvReturn] = useState(5);
  const [fedTax, setFedTax] = useState(25);
  const [stateTax, setStateTax] = useState(0);
  const [filing, setFiling] = useState('married');
  const [horizon, setHorizon] = useState(term);

  const c = useMemo(() => {
    const downAmt = homePrice * downPct / 100;
    const loan = homePrice - downAmt;
    const r = rate / 100 / 12, n = term * 12;
    const M = r === 0 ? loan / n : (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const buyClosingAmt = homePrice * buyClosing / 100;
    let bal = loan;
    let totalBuy = downAmt + buyClosingAmt;
    let totalRent = deposit + upfrontCost;
    let hv = homePrice, curRent = rent, curTax = homePrice * propTax / 100;
    let curIns = homeIns, curHoa = hoa, curMaint = homePrice * maint / 100;
    const rows = [];
    let breakEven = null;
    for (let y = 1; y <= horizon; y++) {
      for (let m = 0; m < 12; m++) {
        if (y > term) break;
        const interest = bal * r;
        const princ = M - interest;
        bal = Math.max(0, bal - princ);
      }
      const yearPmt = y <= term ? M * 12 : 0;
      const yearCarry = yearPmt + curTax + curIns + curHoa + curMaint;
      totalBuy += yearCarry;
      totalRent += curRent * 12 + renterIns * 12;
      hv *= (1 + appr / 100);
      curRent *= (1 + rentInc / 100);
      curTax *= (1 + propTaxInc / 100);
      curIns *= (1 + costInc / 100); curHoa *= (1 + costInc / 100); curMaint *= (1 + costInc / 100);
      const netProc = hv * (1 - sellClosing / 100) - bal;
      const buyNetSoFar = totalBuy - netProc;
      if (breakEven == null && buyNetSoFar <= totalRent) breakEven = y;
      rows.push({ year: y, cumBuy: totalBuy, cumRent: totalRent, hv, balance: bal, netProc, buyNet: buyNetSoFar });
    }
    const netProceedsSell = hv * (1 - sellClosing / 100) - bal;
    const buyNetCost = totalBuy - netProceedsSell;
    return { M, downAmt, buyClosingAmt, buyNetCost, totalRent, netProceedsSell, hv, rows, breakEven };
  }, [homePrice, downPct, rate, term, buyClosing, propTax, propTaxInc, homeIns, hoa, maint, appr, costInc, sellClosing, rent, rentInc, renterIns, deposit, upfrontCost, horizon]);

  const buyCheaper = c.buyNetCost < c.totalRent;

  return (
    <CalcShell look="cnet" title="Rent vs. Buy Calculator" subtitle="Compare the long-term cost of buying vs. renting given your specific market and tax situation."
      about={<>
        <p>Whether it's cheaper to <b>rent or buy</b> depends on the price of housing, mortgage rates, expected home appreciation, taxes, and how long you plan to stay. This calculator factors in all major variables including opportunity cost.</p>
        <p><b>Break-even year</b> is the point at which the cumulative cost of buying (net of home equity) falls below the cumulative cost of renting.</p>
      </>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT — Home Purchase */}
        <div>
          <h3 className="text-base font-bold text-slate-900 mb-3 pb-2 border-b border-slate-200">Home Purchase</h3>
          <F label="Home price" v={homePrice} on={setHomePrice} prefix="$" testId="rvb-price" />
          <F label="Down payment" v={downPct} on={setDownPct} suffix="%" />
          <F label="Interest rate" v={rate} on={setRate} suffix="%" step="0.001" />
          <F label="Loan term" v={term} on={setTerm} suffix="years" />
          <F label="Buying closing costs" v={buyClosing} on={setBuyClosing} suffix="%" />
          <F label="Property tax" v={propTax} on={setPropTax} suffix="% / year" step="0.01" />
          <F label="Property tax increase" v={propTaxInc} on={setPropTaxInc} suffix="% / year" />
          <F label="Home insurance" v={homeIns} on={setHomeIns} prefix="$" suffix="/ year" />
          <F label="HOA fee" v={hoa} on={setHoa} prefix="$" suffix="/ year" />
          <F label="Maintenance cost" v={maint} on={setMaint} suffix="% / year" step="0.1" />
          <F label="Home value appreciation" v={appr} on={setAppr} suffix="% / year" step="0.1" />
          <F label="Cost/insurance increase" v={costInc} on={setCostInc} suffix="% / year" />
          <F label="Selling closing costs" v={sellClosing} on={setSellClosing} suffix="%" />
        </div>

        {/* RIGHT — Home Rent + Your Information */}
        <div>
          <h3 className="text-base font-bold text-slate-900 mb-3 pb-2 border-b border-slate-200">Home Rent</h3>
          <F label="Monthly rental fee" v={rent} on={setRent} prefix="$" testId="rvb-rent" />
          <F label="Rental fee increase" v={rentInc} on={setRentInc} suffix="% / year" />
          <F label="Renter's insurance" v={renterIns} on={setRenterIns} prefix="$" suffix="/ month" />
          <F label="Security deposit" v={deposit} on={setDeposit} prefix="$" />
          <F label="Upfront cost" v={upfrontCost} on={setUpfrontCost} prefix="$" />

          <h3 className="text-base font-bold text-slate-900 mb-3 pb-2 border-b border-slate-200 mt-6">Your Information</h3>
          <F label="Average investment return" v={invReturn} on={setInvReturn} suffix="%" step="0.1" />
          <F label="Marginal federal tax rate" v={fedTax} on={setFedTax} suffix="%" />
          <F label="Marginal state tax rate" v={stateTax} on={setStateTax} suffix="%" />
          <div className="field">
            <label>Tax filing status</label>
            <select value={filing} onChange={e => setFiling(e.target.value)}>
              <option value="single">single</option>
              <option value="married">married filing jointly</option>
              <option value="mfs">married filing separately</option>
              <option value="hoh">head of household</option>
              <option value="widow">qualifying widow(er)</option>
            </select>
          </div>
          <F label="Comparison horizon" v={horizon} on={setHorizon} suffix="years" />
        </div>
      </div>

      {/* RESULT SUMMARY */}
      <div className="mt-8 rounded-lg overflow-hidden border border-slate-200">
        <div className="px-4 py-3 text-white font-bold" style={{ background: '#4c7b25' }} data-testid="rvb-result-header">
          After {horizon} years, {buyCheaper ? 'Buying' : 'Renting'} is cheaper by {fmt(Math.abs(c.buyNetCost - c.totalRent))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200 bg-white">
          <SummaryTile title="Total Cost — Buy (net)" value={fmt(c.buyNetCost)} highlight={buyCheaper} testId="rvb-buy" />
          <SummaryTile title="Total Cost — Rent" value={fmt(c.totalRent)} highlight={!buyCheaper} testId="rvb-rent-total" />
          <SummaryTile title="Break-even Year" value={c.breakEven ? `Year ${c.breakEven}` : `> ${horizon} yr`} testId="rvb-breakeven" />
        </div>
      </div>

      {/* CHART */}
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Cumulative Cost — Buy (net) vs Rent</h3>
        {(() => {
          const w = 720, h = 300, padL = 70, padR = 20, padT = 15, padB = 36;
          const rows = c.rows;
          if (!rows.length) return null;
          const max = Math.max(...rows.map(r => Math.max(r.buyNet, r.cumRent)));
          const xs = i => padL + (i / (rows.length - 1 || 1)) * (w - padL - padR);
          const ys = v => h - padB - ((v - 0) / (max || 1)) * (h - padT - padB);
          const buyLine = rows.map((r, i) => `${xs(i)},${ys(r.buyNet)}`).join(' ');
          const rentLine = rows.map((r, i) => `${xs(i)},${ys(r.cumRent)}`).join(' ');
          const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => t * max);
          const xTicks = rows.length <= 10 ? rows : rows.filter((_, i) => i % Math.ceil(rows.length / 8) === 0);
          return (
            <div className="overflow-auto">
              <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[520px]">
                {/* Grid */}
                {ticks.map((t, i) => (
                  <g key={i}>
                    <line x1={padL} y1={ys(t)} x2={w - padR} y2={ys(t)} stroke="#e2e8f0" strokeDasharray="3 3" />
                    <text x={padL - 8} y={ys(t) + 4} textAnchor="end" fontSize="11" fill="#64748b">${(t / 1000).toFixed(0)}K</text>
                  </g>
                ))}
                {/* Break-even marker */}
                {c.breakEven && (
                  <g>
                    <line x1={xs(c.breakEven - 1)} y1={padT} x2={xs(c.breakEven - 1)} y2={h - padB} stroke="#dc2626" strokeDasharray="4 3" strokeWidth="1.5" />
                    <text x={xs(c.breakEven - 1) + 6} y={padT + 12} fontSize="11" fill="#dc2626" fontWeight="bold">Break-even Y{c.breakEven}</text>
                  </g>
                )}
                <polyline points={buyLine} fill="none" stroke="#2563eb" strokeWidth="2.5" />
                <polyline points={rentLine} fill="none" stroke="#f97316" strokeWidth="2.5" />
                {xTicks.map((r, i) => (
                  <text key={i} x={xs(rows.indexOf(r))} y={h - padB + 16} fontSize="10" fill="#64748b" textAnchor="middle">Y{r.year}</text>
                ))}
                <line x1={padL} y1={h - padB} x2={w - padR} y2={h - padB} stroke="#94a3b8" />
                <line x1={padL} y1={padT} x2={padL} y2={h - padB} stroke="#94a3b8" />
              </svg>
              <div className="flex gap-6 justify-center mt-3 text-sm">
                <span className="flex items-center gap-2"><span className="w-4 h-3 rounded-sm" style={{ background: '#2563eb' }} />Cumulative Buy (net of equity)</span>
                <span className="flex items-center gap-2"><span className="w-4 h-3 rounded-sm" style={{ background: '#f97316' }} />Cumulative Rent</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* TABLE */}
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Year-by-Year Cumulative Cost</h3>
        <div className="overflow-auto max-h-96 border border-slate-200 rounded">
          <table className="w-full text-sm">
            <thead className="sticky top-0" style={{ background: '#1e3a8a', color: '#fff' }}>
              <tr>
                <th className="px-3 py-2 text-left">Year</th>
                <th className="px-3 py-2 text-right">Cumulative Buy (gross)</th>
                <th className="px-3 py-2 text-right">Home Equity Value</th>
                <th className="px-3 py-2 text-right">Buy Net Cost</th>
                <th className="px-3 py-2 text-right">Cumulative Rent</th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map((r, i) => (
                <tr key={i} className={`border-b ${i % 2 ? 'bg-slate-50' : ''} ${c.breakEven === r.year ? 'bg-yellow-50 font-semibold' : ''}`}>
                  <td className="px-3 py-2">{r.year}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.cumBuy)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.netProc)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.buyNet)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.cumRent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}

function F({ label, v, on, prefix, suffix, step, testId }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="flex gap-2">
        {prefix && <span className="self-center text-slate-500 text-sm">{prefix}</span>}
        <input
          type="number"
          step={step || 'any'}
          value={v}
          onChange={e => on(+e.target.value || 0)}
          className="flex-1 min-w-0"
          data-testid={testId}
        />
        {suffix && <span className="self-center text-slate-500 text-sm whitespace-nowrap">{suffix}</span>}
      </div>
    </div>
  );
}

function SummaryTile({ title, value, highlight, testId }) {
  return (
    <div className={`px-4 py-4 text-center ${highlight ? 'bg-green-50' : ''}`}>
      <div className="text-xs uppercase tracking-wide text-slate-600 mb-1">{title}</div>
      <div className={`text-2xl font-bold ${highlight ? 'text-green-700' : 'text-slate-900'}`} data-testid={testId}>{value}</div>
    </div>
  );
}
